import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Card,
  IconButton,
  Typography,
  Input,
  Button,
  Avatar,
  Chip,
  CircularProgress,
} from '@mui/joy';
import {
  ChatCircle as ChatIcon,
  X as CloseIcon,
  PaperPlaneTilt as SendIcon,
  Sparkle as AIIcon,
  User as UserIcon,
  Lightbulb as TipIcon,
  Question as HelpIcon,
  Gauge as QuickIcon,
} from '@phosphor-icons/react';
import { useMutation } from '@tanstack/react-query';

interface Message {
  id: string;
  type: 'user' | 'ai' | 'suggestion';
  content: string;
  timestamp: Date;
  actions?: Array<{
    label: string;
    action: () => void;
  }>;
}

interface Suggestion {
  id: string;
  icon: React.ReactNode;
  label: string;
  prompt: string;
}

const QUICK_SUGGESTIONS: Suggestion[] = [
  {
    id: 'improve-seo',
    icon: <TipIcon />,
    label: 'How can I improve my SEO?',
    prompt: 'What are the best ways to improve my website SEO for better Google rankings?',
  },
  {
    id: 'add-content',
    icon: <HelpIcon />,
    label: 'What content should I add?',
    prompt: 'What pages or content should I add to make my roofing website more effective?',
  },
  {
    id: 'quick-changes',
    icon: <QuickIcon />,
    label: 'Quick wins for my site',
    prompt: 'What are some quick changes I can make to improve conversions on my website?',
  },
];

interface AIChatAssistantProps {
  context?: {
    pageId?: string;
    siteId?: string;
    currentPage?: string;
  };
}

export const AIChatAssistant: React.FC<AIChatAssistantProps> = ({ context }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      type: 'ai',
      content: "Hi! I'm your AI assistant. I can help you improve your website, write content, and answer questions. What would you like help with?",
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const chatMutation = useMutation({
    mutationFn: async (message: string) => {
      // This would call your AI chat endpoint
      // For now, we'll simulate a response
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({
            response: "I understand you want help with that. Here's what I suggest:\n\n1. First, make sure your page titles are descriptive and include your main keywords\n2. Add your city name to important pages\n3. Include customer testimonials\n\nWould you like me to help you implement any of these suggestions?",
            actions: [
              { label: 'Update SEO Settings', action: 'navigate:/seo' },
              { label: 'Add Testimonials', action: 'navigate:/testimonials' },
            ],
          });
        }, 1000);
      });
    },
  });

  const sendMessage = async (content: string) => {
    if (!content.trim()) return;

    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);
    setInput('');

    // Get AI response
    try {
      const response = await chatMutation.mutateAsync(content);
      
      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: (response as any).response,
        timestamp: new Date(),
        actions: (response as any).actions?.map((action: any) => ({
          label: action.label,
          action: () => {
            if (action.action.startsWith('navigate:')) {
              window.location.href = action.action.replace('navigate:', '');
            }
          },
        })),
      };
      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: "I'm having trouble connecting right now. Please try again in a moment.",
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    }
  };

  const handleSuggestionClick = (suggestion: Suggestion) => {
    sendMessage(suggestion.prompt);
  };

  if (!isOpen) {
    return (
      <Box sx={{ transition: 'opacity 0.3s', opacity: 1 }}>
        <IconButton
          size="lg"
          variant="solid"
          color="primary"
          onClick={() => setIsOpen(true)}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            boxShadow: 'lg',
            zIndex: 1000,
            '&:hover': {
              transform: 'scale(1.1)',
            },
            transition: 'transform 0.2s',
          }}
        >
          <ChatIcon />
        </IconButton>
      </Box>
    );
  }

  if (isMinimized) {
    return (
      <Card
        variant="solid"
        color="primary"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          width: 320,
          cursor: 'pointer',
          boxShadow: 'lg',
          zIndex: 1000,
        }}
        onClick={() => setIsMinimized(false)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
          <AIIcon style={{ marginRight: 8 }} />
          <Typography level="title-md" sx={{ color: 'white', flex: 1 }}>
            AI Assistant
          </Typography>
          <IconButton
            size="sm"
            variant="plain"
            sx={{ color: 'white' }}
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(false);
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Card>
    );
  }

  return (
    <Box sx={{ transition: 'opacity 0.3s', opacity: 1 }}>
      <Card
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          width: 380,
          height: 600,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 'xl',
          zIndex: 1000,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            bgcolor: 'var(--brand-text, #7013f5)',
            color: 'white',
          }}
        >
          <AIIcon style={{ marginRight: 8 }} />
          <Typography level="title-md" sx={{ flex: 1, color: 'white' }}>
            AI Assistant
          </Typography>
          <IconButton
            size="sm"
            variant="plain"
            sx={{ color: 'white', mr: 1 }}
            onClick={() => setIsMinimized(true)}
          >
            −
          </IconButton>
          <IconButton
            size="sm"
            variant="plain"
            sx={{ color: 'white' }}
            onClick={() => setIsOpen(false)}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Messages */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {messages.map((message) => (
            <Box
              key={message.id}
              sx={{
                display: 'flex',
                gap: 1,
                flexDirection: message.type === 'user' ? 'row-reverse' : 'row',
              }}
            >
              <Avatar
                size="sm"
                sx={{
                  bgcolor: message.type === 'user' ? 'primary.500' : 'neutral.500',
                }}
              >
                {message.type === 'user' ? <UserIcon /> : <AIIcon />}
              </Avatar>
              <Box sx={{ flex: 1, maxWidth: '80%' }}>
                <Card
                  variant={message.type === 'user' ? 'solid' : 'outlined'}
                  color={message.type === 'user' ? 'primary' : 'neutral'}
                  sx={{
                    px: 2,
                    py: 1,
                  }}
                >
                  <Typography
                    level="body-sm"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      color: message.type === 'user' ? 'white' : 'text.primary',
                    }}
                  >
                    {message.content}
                  </Typography>
                </Card>
                {message.actions && message.actions.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                    {message.actions.map((action, index) => (
                      <Button
                        key={index}
                        size="sm"
                        variant="soft"
                        onClick={action.action}
                      >
                        {action.label}
                      </Button>
                    ))}
                  </Box>
                )}
                <Typography
                  level="body-xs"
                  sx={{ mt: 0.5, color: 'text.secondary', textAlign: message.type === 'user' ? 'right' : 'left' }}
                >
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </Typography>
              </Box>
            </Box>
          ))}
          
          {chatMutation.isPending && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Avatar size="sm" sx={{ bgcolor: 'neutral.500' }}>
                <AIIcon />
              </Avatar>
              <Card variant="outlined" sx={{ px: 2, py: 1 }}>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <CircularProgress size="sm" />
                  <Typography level="body-sm">Thinking...</Typography>
                </Box>
              </Card>
            </Box>
          )}
          
          <div ref={messagesEndRef} />
        </Box>

        {/* Quick Suggestions */}
        {messages.length === 1 && (
          <Box sx={{ px: 2, pb: 2 }}>
            <Typography level="body-xs" sx={{ mb: 1, color: 'text.secondary' }}>
              Quick suggestions:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {QUICK_SUGGESTIONS.map((suggestion) => (
                <Chip
                  key={suggestion.id}
                  startDecorator={suggestion.icon}
                  onClick={() => handleSuggestionClick(suggestion)}
                  sx={{ cursor: 'pointer' }}
                >
                  {suggestion.label}
                </Chip>
              ))}
            </Box>
          </Box>
        )}

        {/* Input */}
        <Box
          sx={{
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            gap: 1,
          }}
        >
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage(input);
              }
            }}
            placeholder="Ask me anything..."
            sx={{ flex: 1 }}
            disabled={chatMutation.isPending}
          />
          <IconButton
            variant="solid"
            color="primary"
            onClick={() => sendMessage(input)}
            disabled={!input.trim() || chatMutation.isPending}
          >
            <SendIcon />
          </IconButton>
        </Box>
      </Card>
    </Box>
  );
};
